import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Column, Table } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

function getColumnLabel<TData>(column: Column<TData, unknown>): string {
  const header = column.columnDef.header;
  if (typeof header === "string") return header;
  const idLabelMap: Record<string, string> = {
    fullName: "Nome",
    jobTitle: "Cargo",
    compDiscipline: "Disciplina Comp",
    jobLevel: "Nível",
    department: "Departamento",
    directManagerId: "Gestor",
    status: "Status",
    currentSalary: "Salário",
    currency: "Moeda",
    compaRatio: "Compa Ratio",
    employmentType: "Tipo Contrato",
    startDate: "Data Início",
    endDate: "Data Fim",
    workType: "Modelo Trabalho",
    corporateEmail: "E-mail Corporativo",
    personalEmail: "E-mail Pessoal",
    phone: "Telefone",
    cpf: "CPF",
    birthdate: "Data Nascimento",
    address: "Endereço",
    sop: "SOP",
    emergencyContact: "Contato Emergência",
    slackId: "Slack ID",
  };
  return idLabelMap[column.id] || column.id;
}

function SortableColumnItem<TData>({ column }: { column: Column<TData, unknown> }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isDragging ? "grabbing" : "grab",
  };

  const isVisible = column.getIsVisible();
  const canHide = column.getCanHide();

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`flex items-center gap-3 px-1 py-3 border-b border-[hsl(var(--card-line))] last:border-b-0 touch-none ${
        isDragging ? "opacity-90 bg-[hsl(var(--sand-step))]" : ""
      }`}
      data-testid={`column-item-${column.id}`}
    >
      <label className="flex items-center gap-3 flex-1 cursor-pointer">
        <input
          type="checkbox"
          className="h-3.5 w-3.5 accent-[hsl(var(--comp-coral))]"
          checked={isVisible}
          disabled={!canHide}
          onPointerDown={(e) => e.stopPropagation()}
          onChange={(e) => column.toggleVisibility(e.target.checked)}
          data-testid={`column-toggle-${column.id}`}
        />
        <span
          className={`text-sm ${isVisible ? "body-strong text-[hsl(var(--ink-primary))]" : "text-[hsl(var(--ink-muted))]"}`}
        >
          {getColumnLabel(column)}
        </span>
      </label>
    </div>
  );
}

interface ColumnManagerSheetProps<TData> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  table: Table<TData>;
  onColumnOrderChange: (newOrder: string[]) => void;
}

export function ColumnManagerSheet<TData>({
  open,
  onOpenChange,
  table,
  onColumnOrderChange,
}: ColumnManagerSheetProps<TData>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const allColumns = table.getAllLeafColumns().filter((col) => col.getCanHide());
  const columnOrder = allColumns.map((col) => col.id);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = columnOrder.indexOf(active.id as string);
    const newIndex = columnOrder.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;

    const fullOrder = table.getAllLeafColumns().map((col) => col.id);

    const hidableIds = allColumns.map((c) => c.id);
    const hidableInFullOrder = fullOrder.filter((id) => hidableIds.includes(id));
    const reordered = arrayMove(hidableInFullOrder, oldIndex, newIndex);

    const newFullOrder: string[] = [];
    let reorderedIdx = 0;
    for (const id of fullOrder) {
      if (hidableIds.includes(id)) {
        newFullOrder.push(reordered[reorderedIdx]);
        reorderedIdx++;
      } else {
        newFullOrder.push(id);
      }
    }

    onColumnOrderChange(newFullOrder);
  }

  function handleResetDefault() {
    onColumnOrderChange([]);
    for (const col of allColumns) {
      col.toggleVisibility(true);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        hideOverlay
        className="glass-drawer w-[360px] sm:w-[360px] flex flex-col p-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
      >
        <SheetHeader className="px-5 pt-6 pb-4 border-b border-[hsl(var(--card-line-glass))]">
          <div className="flex items-center justify-between gap-3">
            <SheetTitle className="text-base">Configurar colunas</SheetTitle>
            <span className="caption-mono text-[hsl(var(--ink-muted))]">Esc para fechar</span>
          </div>
          <SheetDescription className="text-xs text-[hsl(var(--ink-muted))]">
            Marque para mostrar. Arraste pela linha para reordenar.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={columnOrder} strategy={verticalListSortingStrategy}>
              <div className="flex flex-col">
                {allColumns.map((column) => (
                  <SortableColumnItem key={column.id} column={column} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        <SheetFooter className="px-5 py-4 border-t border-[hsl(var(--card-line-glass))] flex !flex-row !justify-between !sm:justify-between gap-3">
          <button
            type="button"
            className="text-sm underline text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink-primary))]"
            onClick={handleResetDefault}
            data-testid="button-reset-columns"
          >
            Restaurar padrão
          </button>
          <Button size="sm" onClick={() => onOpenChange(false)} data-testid="button-apply-columns">
            Aplicar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
